import { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import SftpSettingsMain from "components/batchSetup/SftpSettingsMain";
import pageData from "data/sftpSettingsPage-data.json";
import { loadingPage, selectPartnersData, selectUser } from "store/slices/app";
import * as api from "api/endpoints/sftpSettings";
import downloadByUrl from "utils/downloadByUrl";

export default function SftpSettingsPage() {
  const fileInputRef = useRef(null);
  const [currentView, setCurrentView] = useState("Partner Hosted SFTP Setup");
  const [editableRow, setEditableRow] = useState();
  const [partnerHostedData, setPartnerHostedData] = useState(null);
  const [markaazHostedData, setMarkaazHostedData] = useState(null);
  const [uploadFile, setUploadFile] = useState();
  const [showUploadButton, setShowUploadButton] = useState();
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [markaazLoading, setMarkaazLoading] = useState(false);
  const [disableUploadButton, setDisableUploadButton] = useState(true);
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const [inputValues, setInputValues] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectionStatusAlert, setConnectionStatusAlert] = useState({ type: "", message: "" });
  const [partnerHostedAlert, setPartnerHostedAlert] = useState({ type: "", msg: "" });
  const [markaazHostedAlert, setMarkaazHostedAlert] = useState({ type: "", msg: "" });

  const userData = useSelector(selectUser);
  const partnersData = useSelector(selectPartnersData);
  const epId = userData.selectedPartner.id;
  const dispatch = useDispatch();

  const getDataCallback = useCallback(getData, [epId]);

  useEffect(() => {
    getDataCallback();
    setPartnerHostedAlert(false);
    setMarkaazHostedAlert(false);
    setConnectionStatusAlert(false);
    setDisableSaveButton(true);
  }, [getDataCallback]);

  useEffect(() => {
    if (!markaazHostedData?.keys[0]?.publicKey) {
      setShowUploadButton(true);
    } else {
      setShowUploadButton(false);
    }
  }, [markaazHostedData]);

  useEffect(() => {
    setInputValues({
      userName: partnerHostedData?.connection?.userName || "",
      serverName: partnerHostedData?.connection.serverName || "",
      port: partnerHostedData?.connection.port || ""
    });
  }, [partnerHostedData]);

  useEffect(() => {
    setEditableRow(null);
  }, [partnersData.isPartnerChanged]);

  async function getData() {
    setLoading(true);
    await getPartnerHostedData(epId);
    await getMarkaazHostedData(epId);
    setLoading(false);
  }

  function handleInputChange(inputName, value) {
    setInputValues({
      ...inputValues,
      [inputName]: value
    });

    const inputData = { ...inputValues, [inputName]: value };
    const allValuesSet = Object.values(inputData).every(
      (value) => value !== undefined && value !== null && value !== ""
    );

    if (partnersData.isPartnerChanged) {
      setDisableSaveButton(!allValuesSet);
    }
  }

  function handlePanelChange(event, value) {
    setCurrentView(value);
    setEditableRow(false);
  }

  function handleEdit(index) {
    setEditableRow(index === editableRow ? null : index);
  }

  function handleDownload(fileLink) {
    downloadByUrl(fileLink);
  }

  function handleUploadButton() {
    fileInputRef.current.click();
  }

  function handleUploadFile(event) {
    const formData = new FormData();
    const files = event.target.files;
    for (let i = 0; i < files.length; i++) {
      formData.append("publicKey", files[i]);
    }
    formData.append("epId", epId);

    if (partnersData.isPartnerChanged) {
      setDisableUploadButton(false);
    }

    setShowUploadButton(false);
    setUploadFile(formData);
  }

  const handleFileRemove = () => {
    setShowUploadButton(true);
    setDisableUploadButton(true);
  };

  async function handleAlertClose(name) {
    switch (name) {
      case "phs":
        setPartnerHostedAlert({
          type: "",
          message: ""
        });
        break;
      case "tc":
        setConnectionStatusAlert({ type: "", message: "" });
        break;
      case "mhs":
        setMarkaazHostedAlert({
          type: false,
          message: ""
        });
        break;
      default:
        break;
    }
  }

  // Partner Hosted API call
  async function getPartnerHostedData(epId) {
    try {
      const partnerData = await api.getPartnerHosted(epId);
      if (partnerData) {
        setPartnerHostedData(partnerData);
      }
    } catch (error) {
      setPartnerHostedData(null);
    }
  }

  async function handleUpdateConnection(data) {
    setPartnerLoading(true);
    dispatch(loadingPage(true));
    setConnectionStatusAlert(false);
    setPartnerHostedAlert(false);
    const request = {
      connection: {
        userName: data.userName || "",
        serverName: data.serverName || "",
        port: data.port || ""
      }
    };
    try {
      await api.updatePartnerHosted(epId, request);
      setPartnerHostedAlert({
        type: "success",
        message: "Successfully Updated"
      });
    } catch (error) {
      const msg = error.response?.data?.errors || error.response?.data?.message || "Something went wrong";
      setPartnerHostedAlert({
        type: "error",
        message: msg
      });
    } finally {
      setPartnerLoading(false);
      setEditableRow(false);
      setDisableSaveButton(true);
      getPartnerHostedData(epId);
      dispatch(loadingPage(false));
    }
  }

  // Markaaz Hosted API call
  function getMarkaazHostedData(epId) {
    api
      .getMarkaazHosted(epId)
      .then((markaazData) => {
        setMarkaazHostedData(markaazData);
      })
      .catch((error) => {});
  }

  async function updateMarkaazHostedData() {
    setMarkaazLoading(true);
    dispatch(loadingPage(true));
    setMarkaazHostedAlert(false);
    try {
      await api.updateMarkaazHosted(uploadFile);
      setShowUploadButton(false);
      getMarkaazHostedData(epId);
      setMarkaazHostedAlert({
        type: "success",
        message: "Successfully Updated"
      });
    } catch (error) {
      const msg = error.response?.data?.errors || error.response?.data?.message || "Something went wrong";
      setMarkaazHostedAlert({
        type: "error",
        message: msg
      });
      setShowUploadButton(true);
      setDisableUploadButton(true);
    } finally {
      setMarkaazLoading(false);
      setDisableUploadButton(true);
      dispatch(loadingPage(false));
    }
  }

  async function handleTestConnection() {
    setConnectionLoading(true);
    dispatch(loadingPage(true));
    setPartnerHostedAlert(false);
    setConnectionStatusAlert(false);
    const data = { epId: epId };

    try {
      await api.runTestConnection(data);
      setConnectionStatusAlert({ type: "success", message: "Connection successful!" });
    } catch (error) {
      const msg = error.response?.data?.errors || error.response?.data?.message || "Something went wrong";
      setConnectionStatusAlert({ type: "error", message: msg });
    } finally {
      setConnectionLoading(false);
      dispatch(loadingPage(false));
    }
  }

  return (
    <SftpSettingsMain
      loading={loading}
      currentView={currentView}
      showInputBox={editableRow}
      fileInputRef={fileInputRef}
      partnerHostedData={partnerHostedData}
      markaazHostedData={markaazHostedData}
      showUploadButton={showUploadButton}
      partnerLoading={partnerLoading}
      markaazLoading={markaazLoading}
      disableUploadButton={disableUploadButton}
      disableSaveButton={disableSaveButton}
      inputValues={inputValues}
      connectionStatusAlert={connectionStatusAlert}
      connectionLoading={connectionLoading}
      partnerHostedAlert={partnerHostedAlert}
      markaazHostedAlert={markaazHostedAlert}
      saveDisable
      pageData={pageData}
      onPanelChange={handlePanelChange}
      onEdit={handleEdit}
      onUpload={handleUploadButton}
      onFileInput={handleUploadFile}
      onSubmit={handleUpdateConnection}
      onDownload={handleDownload}
      onSaveMarkaazHosted={updateMarkaazHostedData}
      onFileRemove={handleFileRemove}
      onInputChange={handleInputChange}
      onTestConnection={handleTestConnection}
      onAlertClose={handleAlertClose}
    />
  );
}
